import React, { useState, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/auth"; // Verifique o caminho

// Função auxiliar para formatar data
const formatarDataHora = (isoString) => {
    if (!isoString) return '—';
    try {
        const data = new Date(isoString);
        return data.toLocaleString('pt-BR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    } catch (e) {
        return 'Data inválida';
    }
};

// Função auxiliar para calcular total de unidades em um item de movimentação
const calcularTotalUnidadesMovItem = (movItem) => {
    if (!movItem) return 0;
    // Se tem grade detalhada no JSON (vem do backend como objeto já)
    if (movItem.tamanhos_do_item && Array.isArray(movItem.tamanhos_do_item) && movItem.tamanhos_do_item.length > 0) {
        return movItem.tamanhos_do_item.reduce((sum, t) => sum + (Number(t.quantidade) || 0), 0);
    }
    // Se for item simples, usa a quantidade da linha principal (mov_item_quantidade ou quantidade)
    return Number(movItem.mov_item_quantidade || movItem.quantidade || 0);
};


function ModalEstornarConclusao({ isVisible, onClose, producaoId, numeroOp, onEstornoSucesso }) {
    const { currentUser } = useAuth();
    const [movimentacoesConclusao, setMovimentacoesConclusao] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [deletingId, setDeletingId] = useState(null);

    // Hook para chamadas API
    const apiCall = useCallback(async (url, method = 'GET', data = null) => {
        // ... (código apiCall sem alterações)
        if (!currentUser) throw new Error("Usuário não autenticado.");
        const token = await currentUser.getIdToken();
        const API_URL = process.env.REACT_APP_API_URL;
        const config = {
            method,
            headers: { 'Authorization': `Bearer ${token}` }
        };
         if (data && method !== 'GET' && method !== 'HEAD') {
            config.headers['Content-Type'] = 'application/json';
            config.body = JSON.stringify(data);
        }
        const response = await fetch(`${API_URL}${url}`, config);
        if (!response.ok) {
             const errorData = await response.json().catch(() => ({}));
             throw new Error(errorData.error || `Falha na requisição (${response.status})`);
        }
         if (response.status === 204) return null;
        return response.json();
    }, [currentUser]);

    // Função para buscar as movimentações de conclusão
    const fetchMovimentacoes = useCallback(async () => {
        if (!isVisible || !numeroOp) return;
        setLoading(true);
        setError('');
        try {
            // Assumindo que GET /api/movimentacoes suporta estes filtros
            const params = new URLSearchParams({
                origem: 'Produção',
                documento: numeroOp,
                tipo: 'entrada'
            });
            const dataResumo = await apiCall(`/api/movimentacoes?${params.toString()}`);

            const movimentacoesCompletas = [];
            if (dataResumo && Array.isArray(dataResumo)) {
                // Busca detalhes de cada movimentação para obter os itens
                for (const movResumo of dataResumo) {
                    try {
                        const movCompleta = await apiCall(`/api/movimentacoes/${movResumo.id}`);
                        if (movCompleta && movCompleta.itens && Array.isArray(movCompleta.itens)) {
                            // Calcula o total de unidades ANTES de adicionar
                            movCompleta.totalUnidadesConcluidas = movCompleta.itens.reduce((sum, item) => sum + calcularTotalUnidadesMovItem(item), 0);
                            movimentacoesCompletas.push(movCompleta);
                        }
                    } catch (itemError) {
                         console.error(`Erro ao buscar detalhes da movimentação ${movResumo.id}:`, itemError);
                    }
                }
            }

            setMovimentacoesConclusao(movimentacoesCompletas.sort((a,b) => new Date(b.data_movimentacao) - new Date(a.data_movimentacao)));

        } catch (err) {
            console.error("Erro ao buscar movimentações de conclusão:", err);
            setError(err.message || 'Falha ao carregar histórico de conclusões.');
            setMovimentacoesConclusao([]);
        } finally {
            setLoading(false);
        }
    }, [isVisible, numeroOp, apiCall]);

    // Efeito para buscar as movimentações
    useEffect(() => {
        fetchMovimentacoes();
    }, [fetchMovimentacoes]);

    // Handler para estornar
    const handleEstornar = async (movId) => {
        setDeletingId(movId);
        setError('');
        try {
            await apiCall(`/api/producao/conclusao/${movId}`, 'DELETE');
            Swal.fire('Estornado!', 'A conclusão foi revertida com sucesso.', 'success');
            setMovimentacoesConclusao(prev => prev.filter(mov => mov.id !== movId));
            if (onEstornoSucesso) {
                onEstornoSucesso();
            }
        } catch (err) {
            console.error("Erro ao estornar conclusão:", err);
            // Mostra o erro da API (que agora pode ser "Estoque ficaria negativo...")
            setError(err.message || 'Falha ao estornar.');
            Swal.fire('Erro', err.message || 'Não foi possível estornar a conclusão.', 'error');
        } finally {
            setDeletingId(null);
        }
    };


    // --- Renderização ---
    // A estrutura principal do modal DEVE estar aqui
    if (!isVisible) return null;

    const modalStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 1055 // Z-index alto para ficar sobre outros elementos
    };
    const contentStyle = {
        backgroundColor: 'white', padding: '25px', borderRadius: '8px', width: '90%', maxWidth: '900px', // Maior para tabela
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.5)',
        display: 'flex', flexDirection: 'column', // Para garantir layout interno
        maxHeight: '90vh' // Limita altura máxima
    };

    return (
        // *** ESTES DIVS EXTERNOS SÃO ESSENCIAIS ***
        <div style={modalStyle}>
            <div style={contentStyle}>
                {/* Cabeçalho do Modal */}
                <div className="modal-header border-bottom pb-2 mb-3">
                    <h4 className="modal-title">Estornar Conclusão da OP <span className="text-primary">#{numeroOp}</span></h4>
                    <button type="button" className="btn-close" onClick={onClose} disabled={!!deletingId}></button>
                </div>

                {/* Corpo do Modal */}
                <div className="modal-body p-0" style={{ flexGrow: 1, overflowY: 'auto' }}> {/* Área rolável */}
                    {error && <div className="alert alert-danger mx-3">{error}</div>} {/* Adicionado espaçamento */}

                    {loading && (
                        <div className="text-center p-4"> <div className="spinner-border spinner-border-sm text-primary"></div> Carregando histórico...</div>
                    )}

                    {!loading && movimentacoesConclusao.length === 0 && (
                        <div className="alert alert-warning text-center mx-3">Nenhuma movimentação de conclusão encontrada para esta OP.</div>
                    )}

                    {!loading && movimentacoesConclusao.length > 0 && (
                        <div className="table-responsive px-3"> {/* Adicionado padding lateral */}
                            <table className="table table-sm table-striped table-hover mb-0"> {/* Removido style maxHeight */}
                                <thead className='table-light' style={{position: 'sticky', top: 0, zIndex: 1}}> {/* Cabeçalho fixo */}
                                    <tr>
                                        <th style={{width: '150px'}}>Data Conclusão</th>
                                        <th>Descrição / Item</th>
                                        <th className='text-end' style={{width: '100px'}}>Qtde. Concluída</th>
                                        <th className='text-center' style={{width: '80px'}}>Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {movimentacoesConclusao.map(mov => (
                                        <tr key={mov.id}>
                                            <td>{formatarDataHora(mov.data_movimentacao)}</td>
                                            <td>
                                                {/* Mostra o primeiro item como referência */}
                                                {mov.itens && mov.itens[0] ?
                                                    `${mov.itens[0].item_codigo || '?'} - ${mov.itens[0].item_descricao || 'Item desconhecido'} (Cor: ${mov.itens[0].cor || 'N/A'})`
                                                    : (mov.descricao || 'Detalhes indisponíveis')
                                                }
                                                {mov.itens && mov.itens.length > 1 && ` (+${mov.itens.length - 1} outros)`}
                                            </td>
                                            <td className='text-end fw-bold'>{mov.totalUnidadesConcluidas ?? '?'}</td>
                                            <td className='text-center'>
                                                <button
                                                    className="btn btn-danger btn-sm"
                                                    onClick={() => handleEstornar(mov.id)}
                                                    disabled={deletingId === mov.id}
                                                    title="Estornar esta conclusão"
                                                >
                                                    {deletingId === mov.id ?
                                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        : <i className="fas fa-undo"></i>
                                                    }
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                 {/* Rodapé do Modal */}
                <div className="modal-footer border-top pt-3 mt-3">
                    <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={!!deletingId}>
                        Fechar
                    </button>
                </div>
            </div>
        </div>
        // *** FIM DOS DIVS EXTERNOS ESSENCIAIS ***
    );
}

export default ModalEstornarConclusao;